"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createGovernanceAccountSchema = exports.ProposalOption = exports.Proposal = exports.ACCOUNT_VERSION_V2 = exports.ACCOUNT_VERSION_V1 = exports.deserializeBorsh = exports.VoteThreshold = exports.VoteThresholdType = exports.VoteType = exports.VoteTypeKind = void 0;
const web3_js_1 = require("@solana/web3.js");
const borsh_1 = require("borsh");
borsh_1.BinaryReader.prototype.readPubkey = function () {
    const reader = this;
    const array = reader.readFixedArray(32);
    return new web3_js_1.PublicKey(array);
};
borsh_1.BinaryWriter.prototype.writePubkey = function (value) {
    const writer = this;
    writer.writeFixedArray(value.toBuffer());
};
var VoteTypeKind;
(function (VoteTypeKind) {
    VoteTypeKind[VoteTypeKind["SingleChoice"] = 0] = "SingleChoice";
    VoteTypeKind[VoteTypeKind["MultiChoice"] = 1] = "MultiChoice";
})(VoteTypeKind = exports.VoteTypeKind || (exports.VoteTypeKind = {}));
class VoteType {
    constructor(args) {
        this.type = args.type;
        this.choiceCount = args.choiceCount;
    }
    isSingleChoice() {
        return this.type === VoteTypeKind.SingleChoice;
    }
}
exports.VoteType = VoteType;
VoteType.SINGLE_CHOICE = new VoteType({
    type: VoteTypeKind.SingleChoice,
    choiceCount: undefined,
});
VoteType.MULTI_CHOICE = (choiceCount) => new VoteType({
    type: VoteTypeKind.MultiChoice,
    choiceCount,
});
borsh_1.BinaryReader.prototype.readVoteType = function () {
    const reader = this;
    const value = reader.buf.readUInt8(reader.offset);
    reader.offset += 1;
    if (value === VoteTypeKind.SingleChoice) {
        return VoteType.SINGLE_CHOICE;
    }
    const choiceCount = reader.buf.readUInt16LE(reader.offset);
    return VoteType.MULTI_CHOICE(choiceCount);
};
var VoteThresholdType;
(function (VoteThresholdType) {
    // Approval Quorum
    VoteThresholdType[VoteThresholdType["YesVotePercentage"] = 0] = "YesVotePercentage";
    // Not supported in the current version
    VoteThresholdType[VoteThresholdType["QuorumPercentage"] = 1] = "QuorumPercentage";
    // Supported for VERSION >= 3
    VoteThresholdType[VoteThresholdType["Disabled"] = 2] = "Disabled";
})(VoteThresholdType = exports.VoteThresholdType || (exports.VoteThresholdType = {}));
class VoteThreshold {
    constructor(args) {
        this.type = args.type;
        this.value = args.value;
    }
}
exports.VoteThreshold = VoteThreshold;
borsh_1.BinaryReader.prototype.readVoteThreshold = function () {
    const reader = this;
    // Read VoteThreshold and advance the reader by 1
    const type = reader.buf.readUInt8(reader.offset);
    reader.offset += 1;
    // Read VoteThresholds with u8 value
    if (type === VoteThresholdType.YesVotePercentage
        || type === VoteThresholdType.QuorumPercentage) {
        const percentage = reader.buf.readUInt8(reader.offset);
        reader.offset += 1;
        return new VoteThreshold({ type, value: percentage });
    }
    // Read VoteThresholds without value
    if (type === VoteThresholdType.Disabled) {
        return new VoteThreshold({ type, value: undefined });
    }
    throw new Error(`VoteThresholdType ${type} is not supported`);
};
function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
}
function deserializeField(schema, fieldName, fieldType, reader) {
    try {
        if (typeof fieldType === 'string') {
            return reader[`read${capitalizeFirstLetter(fieldType)}`]();
        }
        if (fieldType instanceof Array) {
            if (typeof fieldType[0] === 'number') {
                return reader.readFixedArray(fieldType[0]);
            }
            return reader.readArray(() => deserializeField(schema, fieldName, fieldType[0], reader));
        }
        if (fieldType.kind === 'option') {
            const option = reader.readU8();
            if (option) {
                return deserializeField(schema, fieldName, fieldType.type, reader);
            }
            return undefined;
        }
        return deserializeStruct(schema, fieldType, reader);
    }
    catch (error) {
        if (error instanceof borsh_1.BorshError) {
            error.addToFieldPath(fieldName);
        }
        throw error;
    }
}
function deserializeStruct(schema, classType, reader) {
    const structSchema = schema.get(classType);
    if (structSchema.kind === 'struct') {
        const result = {};
        for (const [fieldName, fieldType] of schema.get(classType).fields) {
            result[fieldName] = deserializeField(schema, fieldName, fieldType, reader);
        }
        return result;
    }
    throw new borsh_1.BorshError(`Unexpected schema kind: ${structSchema.kind}`);
}
/// Deserializes object from bytes using schema.
function deserializeBorsh(schema, classType, buffer) {
    const reader = new borsh_1.BinaryReader(buffer);
    return deserializeStruct(schema, classType, reader);
}
exports.deserializeBorsh = deserializeBorsh;
exports.ACCOUNT_VERSION_V1 = 1;
exports.ACCOUNT_VERSION_V2 = 2;
class Proposal {
}
exports.Proposal = Proposal;
class ProposalOption {
}
exports.ProposalOption = ProposalOption;
function createGovernanceAccountSchema(accountVersion) {
    return new Map([
        [
            ProposalOption,
            {
                kind: 'struct',
                fields: [
                    ['label', 'string'],
                    ['voteWeight', 'u64'],
                    ['voteResult', 'u8'],
                    ['instructionsExecutedCount', 'u16'],
                    ['instructionsCount', 'u16'],
                    ['instructionsNextIndex', 'u16'],
                ],
            },
        ],
        [
            Proposal,
            {
                kind: 'struct',
                fields: [
                    ['accountType', 'u8'],
                    ['governance', 'pubkey'],
                    ['governingTokenMint', 'pubkey'],
                    ['state', 'u8'],
                    ['tokenOwnerRecord', 'pubkey'],
                    ['signatoriesCount', 'u8'],
                    ['signatoriesSignedOffCount', 'u8'],
                    ...(accountVersion === exports.ACCOUNT_VERSION_V1
                        ? [
                            ['yesVotesCount', 'u64'],
                            ['noVotesCount', 'u64'],
                            ['instructionsExecutedCount', 'u16'],
                            ['instructionsCount', 'u16'],
                            ['instructionsNextIndex', 'u16'],
                        ]
                        : [
                            ['voteType', 'voteType'],
                            ['options', [ProposalOption]],
                            ['denyVoteWeight', { kind: 'option', type: 'u64' }],
                            ['reserved1', 'u8'],
                            ['abstainVoteWeight', { kind: 'option', type: 'u64' }],
                            ['startVotingAt', { kind: 'option', type: 'u64' }],
                        ]),
                    ['draftAt', 'u64'],
                    ['signingOffAt', { kind: 'option', type: 'u64' }],
                    ['votingAt', { kind: 'option', type: 'u64' }],
                    ['votingAtSlot', { kind: 'option', type: 'u64' }],
                    ['votingCompletedAt', { kind: 'option', type: 'u64' }],
                    ['executingAt', { kind: 'option', type: 'u64' }],
                    ['closedAt', { kind: 'option', type: 'u64' }],
                    ['executionFlags', 'u8'],
                    ['maxVoteWeight', { kind: 'option', type: 'u64' }],
                    ...(accountVersion === exports.ACCOUNT_VERSION_V1
                        ? []
                        : [['maxVotingTime', { kind: 'option', type: 'u32' }]]),
                    ['voteThreshold', { kind: 'option', type: 'VoteThreshold' }],
                    ...(accountVersion === exports.ACCOUNT_VERSION_V1
                        ? []
                        : [['reserved', [64]]]),
                    ['name', 'string'],
                    ['descriptionLink', 'string'],
                    ...(accountVersion === exports.ACCOUNT_VERSION_V1
                        ? []
                        : [['vetoVoteWeight', 'u64']]),
                ],
            },
        ],
    ]);
}
exports.createGovernanceAccountSchema = createGovernanceAccountSchema;
